import { __rest } from "tslib";
import { isValidElement } from 'react';
import Styles from './styles';
/**
 * 计算容器样式
 * @param props
 */
export function getWrapperStyle(props) {
    const { customStyle, adaptNotch = false, needScale = false, opacity = 1 } = props;
    const { backgroundColor } = customStyle || {};
    const { wrapper } = Styles.getBaseStyle(adaptNotch, needScale);
    const styles = Object.assign(Object.assign({}, wrapper), { opacity });
    // 设置背景色
    if (backgroundColor) {
        styles.backgroundColor = backgroundColor;
    }
    return styles;
}
/**
 * 计算箭头样式
 * @param title
 * @param adaptNotch
 * @param needScale
 */
export function getArrowStyle(title, adaptNotch, needScale) {
    if (!title || isValidElement(title)) {
        return {};
    }
    const { tintColor } = title || {};
    const { arrow } = Styles.getBaseStyle(adaptNotch, needScale);
    if (tintColor) {
        return Object.assign(Object.assign({}, arrow), { color: tintColor });
    }
    return arrow;
}
/**
 * 计算标题样式
 * @param title
 * @param adaptNotch
 * @param needScale
 */
export function getTitleStyle(title, adaptNotch, needScale) {
    // 默认样式
    const defaultStyle = { title: {}, subtitle: {} };
    // 如果是react元素，则直接返回空样式
    if (!title || isValidElement(title)) {
        return defaultStyle;
    }
    const { numberOfLines = 0, tintColor, subTintColor } = title || {};
    const { title: titleStyle = defaultStyle, multiTitle = defaultStyle } = Styles.getBaseStyle(adaptNotch, needScale);
    let styles = titleStyle;
    if (numberOfLines > 1) {
        styles = multiTitle;
    }
    // 主标题颜色
    if (tintColor) {
        styles = Object.assign(Object.assign({}, styles), { title: Object.assign(Object.assign({}, styles.title), { color: tintColor }) });
    }
    // 副标题颜色
    if (subTintColor) {
        styles = Object.assign(Object.assign({}, styles), { subtitle: Object.assign(Object.assign({}, styles.subtitle), { color: subTintColor }) });
    }
    return styles;
}
/**
 * 计算按钮样式
 * @param config
 * @param adaptNotch
 * @param needScale
 */
export function getButtonStyles(config, adaptNotch, needScale) {
    const buttonStyle = Styles.getButtonStyle(adaptNotch, needScale);
    // 如果按钮配置为空，直接返回空数组
    if (!Array.isArray(config) || !config.length) {
        return [];
    }
    return config.map((item) => {
        // 如果是一个元素的话，不设置样式
        if (isValidElement(item)) {
            return {};
        }
        const { text, icon, tintColor } = item;
        const { pureIcon, pureText, text: textStyle, icon: iconStyle, container, pureTextContainer } = buttonStyle;
        const style = {};
        // 只存在文字时
        if (text && !icon) {
            style.text = Object.assign({}, pureText);
            style.container = pureTextContainer;
        }
        else if (!text && icon) {
            // 只存在按钮时
            style.icon = Object.assign({}, pureIcon);
            style.container = container;
        }
        else if (text && icon) {
            // 文字按钮都存在时
            style.text = Object.assign({}, textStyle);
            style.icon = Object.assign({}, iconStyle);
            style.container = container;
        }
        // 设置字体颜色
        if (style.text && tintColor) {
            style.text.color = tintColor;
        }
        // 设置icon颜色
        if (style.icon && tintColor) {
            style.icon.color = tintColor;
        }
        return style;
    });
}
/**
 * 计算标题
 * @param title
 * @param maxSize
 * @param line
 */
export function computeTitle(title, maxSize, line) {
    // 只有一行且有最大字数限制时才截取
    if (line === 1 && maxSize && title.length > maxSize) {
        return `${title.slice(0, maxSize)}...`;
    }
    return title;
}
/**
 * 获取容器背景色
 * @param computedStyles
 */
export function computeWrapperBgColor(computedStyles) {
    const { wrapper } = computedStyles;
    const { backgroundColor = '#FFFFFF' } = wrapper;
    return backgroundColor;
}
export function getPresetStyle(props) {
    const { mode } = props, innerProps = __rest(props, ["mode"]);
    // 如果没有使用预设模式，则直接返回内部需要的props
    if (!mode) {
        return innerProps;
    }
    // 获取预设样式
    const presetStyle = Styles.presetModeStyle[mode];
    // 使用模式错误
    if (!presetStyle) {
        // eslint-disable-next-line no-console
        console.warn(`预设样式[${mode}]不存在，"mode"参数必须为："blank" | "brand" | "ghost" | "crystal"之一`);
        return innerProps;
    }
    // 设置背景色
    innerProps.customStyle = Object.assign(Object.assign({}, innerProps.customStyle), { backgroundColor: presetStyle.backgroundColor });
    // 如果标题不是reactElement，则设置预设样式
    if (innerProps.title && !isValidElement(innerProps.title)) {
        innerProps.title.tintColor = presetStyle.color;
        innerProps.title.subTintColor = presetStyle.subColor;
    }
    // 设置按钮区域颜色
    const adjustButtonStyle = (buttons) => {
        buttons.forEach((item) => {
            // 如果是一个元素的话，不做任何操作
            if (isValidElement(item)) {
                return null;
            }
            item.tintColor = presetStyle.icon;
            return undefined;
        });
    };
    // 设置左侧按钮
    if (Array.isArray(innerProps.leftButton) && innerProps.leftButton.length) {
        adjustButtonStyle(innerProps.leftButton);
    }
    // 设置右侧按钮
    if (Array.isArray(innerProps.rightButton) &&
        innerProps.rightButton.length) {
        adjustButtonStyle(innerProps.rightButton);
    }
    return innerProps;
}
/**
 * 计算样式
 * @param props
 */
export function getNavBarStyles(props) {
    const { leftButton, rightButton, title, adaptNotch = false, needScale = false } = props;
    const styles = {
        wrapper: getWrapperStyle(props),
        title: getTitleStyle(title, adaptNotch, needScale),
        arrow: getArrowStyle(title, adaptNotch, needScale)
    };
    // 设置左侧按钮样式
    if (Array.isArray(leftButton)) {
        styles.leftButton = getButtonStyles(leftButton, adaptNotch, needScale);
    }
    // 设置右侧按钮样式
    if (Array.isArray(rightButton)) {
        styles.rightButton = getButtonStyles(rightButton, adaptNotch, needScale);
    }
    return styles;
}
/**
 * 计算标题左右偏移量
 * @param layout
 * @param buttonWidth
 * @param props
 */
export function computeTitleMargin(layout, buttonWidth, props) {
    const { titleMargin } = layout;
    const { left, right } = buttonWidth;
    const { title, leftButton, rightButton } = props;
    // [特殊场景]：如果title不是一个自定义元素&标题存在&标题展示超过1行，且左右都有按钮时，标题相对按钮左右留20后自居中
    if (!isValidElement(title) &&
        title &&
        (title.numberOfLines || 1) > 1 && (leftButton === null || leftButton === void 0 ? void 0 : leftButton.length) && (rightButton === null || rightButton === void 0 ? void 0 : rightButton.length)) {
        return {
            marginLeft: left + titleMargin,
            marginRight: right + titleMargin
        };
    }
    const buttonTotalWidth = left + right;
    // 调整标题左右margin
    return {
        marginHorizontal: buttonTotalWidth / 2 + titleMargin
    };
}
